import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { QuizAppearance } from "@/lib/utils/lomdaV3/types";

/** The theme tint (%) an unset answer colour keeps; matches the classes below. */
export const ANSWER_COLOR_TINTS = {
    correctBackground: 10,
    correctBorder: 60,
    wrongBackground: 5,
    wrongBorder: 60,
} as const;

/** Stored colours ride on CSS vars so the selected / hover / after-Check
 *  classes still win over them. An unset field adds nothing. */
export function quizSurfaceStyle(
    a: QuizAppearance | undefined
): CSSProperties | undefined {
    const style: Record<string, string | number> = {};
    if (a?.background) style["--quiz-bg"] = a.background;
    if (a?.border) style["--quiz-border"] = a.border;
    if (a?.correctBackground) style["--quiz-correct-bg"] = a.correctBackground;
    if (a?.correctBorder) style["--quiz-correct-border"] = a.correctBorder;
    if (a?.wrongBackground) style["--quiz-wrong-bg"] = a.wrongBackground;
    if (a?.wrongBorder) style["--quiz-wrong-border"] = a.wrongBorder;
    if (typeof a?.borderWidth === "number") style.borderWidth = a.borderWidth;
    return Object.keys(style).length > 0 ? (style as CSSProperties) : undefined;
}

export function optionCardClass(
    a: QuizAppearance | undefined,
    {
        selected,
        checked,
        correct,
        topAligned,
    }: {
        selected: boolean;
        checked: boolean;
        correct: boolean;
        topAligned: boolean;
    }
) {
    return cn(
        "lomda-v3-card-text flex items-center gap-3 rounded-2xl border bg-card p-4 text-start lomda-v3-text-p shadow-sm transition disabled:cursor-default",
        a?.background && "bg-[color:var(--quiz-bg)]",
        a?.border && "border-[color:var(--quiz-border)]",
        topAligned && "flex-wrap content-start",
        !checked &&
            (selected
                ? "border-primary ring-1 ring-primary/30"
                : "hover:border-primary/50"),
        checked &&
            correct && [
                a?.correctBorder
                    ? "border-[color:var(--quiz-correct-border)]"
                    : "border-[hsl(var(--lomda-success))]/60",
                a?.correctBackground
                    ? "bg-[color:var(--quiz-correct-bg)]"
                    : "bg-[hsl(var(--lomda-success))]/10",
            ],
        checked &&
            selected &&
            !correct && [
                a?.wrongBorder
                    ? "border-[color:var(--quiz-wrong-border)]"
                    : "border-[hsl(var(--lomda-destructive))]/60",
                a?.wrongBackground
                    ? "bg-[color:var(--quiz-wrong-bg)]"
                    : "bg-[hsl(var(--lomda-destructive))]/5",
            ]
    );
}

export function openAnswerClass(a: QuizAppearance | undefined) {
    return cn(
        a?.background
            ? "lomda-v3-card-text bg-[color:var(--quiz-bg)]"
            : "lomda-v3-card-surface",
        "min-h-[120px] w-full resize-none rounded-xl border p-4 lomda-v3-text-p outline-none transition placeholder:text-[hsl(var(--lomda-muted-text))] focus:border-primary disabled:cursor-default",
        a?.border && "border-[color:var(--quiz-border)]"
    );
}
